"use client";

import { IconCircleCheck } from "central-icons/IconCircleCheck";
import { IconCircleX } from "central-icons/IconCircleX";
import { useState } from "react";
import "@/styles/enter-animation.css";
import DemoFooter from "@/components/preview/demo-footer";
import DemoWrapper from "@/components/preview/demo-wrapper";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type AnimationType = "none" | "grouped" | "individual";

const title = "Build software that never breaks";
const description =
  "Interfere is the experience layer for modern product teams.";

const BUTTONS = [
  { label: "Request a demo", variant: "primary" },
  { label: "Join waitlist", variant: "secondary" },
] as const;

const MODES: { icon: typeof IconCircleX; id: AnimationType; label: string }[] =
  [
    { icon: IconCircleX, id: "none", label: "Block" },
    { icon: IconCircleCheck, id: "grouped", label: "Sections" },
    { icon: IconCircleCheck, id: "individual", label: "Individual" },
  ];

function stagger(index: number) {
  return { "--stagger": index } as React.CSSProperties;
}

interface Variant {
  buttonRow: number | null;
  buttons: number | null;
  description: number | null;
  root: string;
  title: number | "words" | null;
}

const VARIANTS: Record<AnimationType, Variant> = {
  grouped: {
    buttonRow: 3,
    buttons: null,
    description: 2,
    root: "animate-enter-individual",
    title: 1,
  },
  individual: {
    buttonRow: null,
    buttons: 7,
    description: 6,
    root: "animate-enter-individual-title",
    title: "words",
  },
  none: {
    buttonRow: null,
    buttons: null,
    description: null,
    root: "animate-enter",
    title: null,
  },
};

const TITLE_CLASS =
  "font-medium text-2xl text-gray-1200 tracking-tight sm:text-3xl";

function Enter({
  children,
  className,
  slot,
}: {
  children: React.ReactNode;
  className?: string;
  slot: number | null;
}) {
  return (
    <div
      className={cn(className, slot !== null && "animate-enter")}
      style={slot === null ? undefined : stagger(slot)}
    >
      {children}
    </div>
  );
}

function Hero({ type }: { type: AnimationType }) {
  const variant = VARIANTS[type];

  return (
    <div
      className={cn(
        "flex h-full w-full select-none flex-col items-start justify-center p-6 sm:items-center sm:px-10",
        variant.root
      )}
    >
      <div className="flex flex-col items-start gap-4">
        <div className="flex w-full flex-col gap-2">
          {variant.title === "words" ? (
            <div className="max-w-80 text-pretty">
              {title.split(" ").map((word, i, arr) => (
                <span
                  className={cn("animate-enter-title", TITLE_CLASS)}
                  key={word}
                  style={stagger(i + 1)}
                >
                  {word}
                  {i < arr.length - 1 && <>&nbsp;</>}
                </span>
              ))}
            </div>
          ) : (
            <Enter
              className={cn("max-w-80 text-pretty", TITLE_CLASS)}
              slot={variant.title}
            >
              {title}
            </Enter>
          )}
          <Enter
            className="max-w-80 text-pretty text-black/50 text-sm dark:text-white/50"
            slot={variant.description}
          >
            {description}
          </Enter>
        </div>
        <Enter className="flex gap-2" slot={variant.buttonRow}>
          {BUTTONS.map((button, i) => (
            <Enter
              key={button.label}
              slot={variant.buttons === null ? null : variant.buttons + i}
            >
              <Button rounded size="sm" type="button" variant={button.variant}>
                {button.label}
              </Button>
            </Enter>
          ))}
        </Enter>
      </div>
    </div>
  );
}

export default function AnimationStaggerDemo() {
  const [selected, setSelected] = useState<AnimationType>("none");

  return (
    <DemoWrapper height={{ base: 440, sm: 300 }} margin="">
      <Hero key={selected} type={selected} />
      <DemoFooter className="flex flex-col gap-2 sm:flex-row">
        {MODES.map(({ icon: Icon, id, label }) => (
          <Button
            className={cn("w-full", selected === id && "bg-gray-200")}
            key={id}
            leftIcon={<Icon className="size-4 text-gray-1000" />}
            onClick={() => setSelected(id)}
            size="sm"
            type="button"
            variant="secondary"
          >
            {label}
          </Button>
        ))}
      </DemoFooter>
    </DemoWrapper>
  );
}
